import buildExcel, { type TExportType } from './spreadsheet';
import type { Table } from '@tanstack/react-table';

import { Analytics } from '@cobliteam/analytics';

import DropdownMenu from '@cobliteam/ui/components/DropdownMenu';

import DataTableExport from '../DataTableExport';

export interface ExportProps<TData> {
  exportDef: ExportDef<TData>;
  data: TData[];
  table: Table<TData>;
  defaultOpen?: boolean;
}

type IOnHandleExportProps = {
  filter?: boolean;
  exportType?: TExportType;
  copy?: boolean;
};

export type ExportDef<T> = {
  title: string;
  fileName: string;
  data: ExportDefData<T>[];
};

export type ExportDefData<T> = {
  id: string;
  accessorKey?: keyof T;
  columnName?: string;
  row?: (row: T) => string;
};

const Export = <TData,>({ table, data, exportDef, defaultOpen = false }: ExportProps<TData>) => {
  const { globalFilter, sorting, columnFilters } = table.getState();

  // check if has filter apply
  const hasFilterApply = globalFilter || !!columnFilters.length || !!sorting.length;

  // get all columns from table
  const allColumns = table.getAllColumns().map((column) => column);

  // get all rows from table
  const allRows = table.getRowModel().rows.map((row) => row.original);

  // get columns from exportDef
  const columns = exportDef.data.map((item) => {
    const column = allColumns.find((c) => c.id === item.id);
    return item?.columnName || column?.columnDef?.meta?.name || 'Sem nome da coluna';
  });

  // get rows from exportDef
  const dataRows = () =>
    data.map((row) =>
      exportDef.data.map((item) => {
        if (item.row) return item.row(row);
        return item.accessorKey ? row[item.accessorKey] : '';
      })
    );

  // get rows from table filtered and apply exportDef
  const tableRows = () =>
    allRows.map((original) => {
      return exportDef.data.map((item) => {
        if (item.row) return item.row(original);
        return item.accessorKey ? original[item.accessorKey] : '';
      });
    });

  const onHandleExport = (params?: IOnHandleExportProps) => {
    const { exportType = 'xlsx', copy = false, filter: filtered } = params || {};

    const rows = filtered ? tableRows() : dataRows();

    Analytics.track('DataTable Exported', { exportType });

    buildExcel({
      data,
      rows,
      columns,
      copy,
      exportType,
      title: exportDef.title || 'Dados exportados',
      fileName: exportDef.fileName || 'dados-exportados',
    });
  };

  return (
    <DataTableExport defaultOpen={defaultOpen}>
      <DropdownMenu.Item onClick={() => onHandleExport()}>
        <DropdownMenu.Label>Exportar todos os dados (excel)</DropdownMenu.Label>
      </DropdownMenu.Item>
      <DropdownMenu.Item onClick={() => onHandleExport({ exportType: 'csv' })}>
        <DropdownMenu.Label>Exportar todos os dados (csv)</DropdownMenu.Label>
      </DropdownMenu.Item>
      <DropdownMenu.Separator />

      <DropdownMenu.Item onClick={() => onHandleExport({ filter: true })} disabled={!hasFilterApply}>
        <DropdownMenu.Label>Exportar dados filtrados (excel)</DropdownMenu.Label>
      </DropdownMenu.Item>
      <DropdownMenu.Item onClick={() => onHandleExport({ filter: true, exportType: 'csv' })} disabled={!hasFilterApply}>
        <DropdownMenu.Label>Exportar dados filtrados (csv)</DropdownMenu.Label>
      </DropdownMenu.Item>
      <DropdownMenu.Separator />
      <DropdownMenu.Item onClick={() => onHandleExport({ copy: true, exportType: 'csv' })}>
        <DropdownMenu.Label>Copiar tabela em CSV</DropdownMenu.Label>
      </DropdownMenu.Item>
    </DataTableExport>
  );
};

export default Export;
